import { prefersReducedMotion, setAriaState } from '@/core/utils/a11y'
import BaseComponent from '@/components/base'

const selectors = {
  soundToggle: '[data-sound-toggle]'
}

const classes = {
  isReady: 'is-ready'
}

export default class GraphicCoverVideo extends BaseComponent {
  static TYPE = 'graphic-cover-video'

  autoPlayEnabled: boolean
  videos: HTMLVideoElement[] = []
  video: HTMLVideoElement | null = null // the one visible at the current breakpoint
  soundToggle: HTMLButtonElement | null = null
  inView: boolean = false
  hasBeenInView: boolean = false
  unlockAutoplay: (() => void) | null = null

  constructor(el: HTMLElement) {
    super(el, {
      watchIntersection: true,
      intersectionOptions: {
        rootMargin: '50px 0px 50px 0px',
      },
      watchBreakpoint: true, // desktop/mobile videos swap on breakpoint change
    })

    this.autoPlayEnabled = prefersReducedMotion() ? false : true

    this.videos = Array.from(this.qsa('video')) as HTMLVideoElement[]
    this.soundToggle = this.qs(selectors.soundToggle) as HTMLButtonElement | null
    this.inView = false

    if (!this.videos.length) {
      console.warn('No video found')
      return
    }

    this.videos.forEach((video) => {
      // Harden autoplay for iOS/Safari: the muted + playsinline *attributes* must be
      // present, not just the JS properties, or mobile blocks muted autoplay.
      video.muted = true
      video.defaultMuted = true
      video.setAttribute('muted', '')
      video.playsInline = true
      video.setAttribute('playsinline', '')

      video.addEventListener('canplay', () => this.onCanPlay(video))
      video.addEventListener('play', () => this.onPlay(video))
      video.addEventListener('playing', () => this.onPlay(video))
      // Proof that frames are actually advancing, whatever became of the events
      // above. Fires within ~250ms of real playback, then detaches.
      video.addEventListener('timeupdate', () => this.onPlay(video), { once: true })
      video.addEventListener('error', (e) => this.onError(e, video))
    })

    this.video = this.selectActiveVideo()

    // The `autoplay` attribute starts the video during HTML parse, while this
    // bundle is still coming down behind `defer`. When muted autoplay wins that
    // race, `play` and `playing` have already fired by the time the listeners
    // above exist, so `is-ready` never lands and the video stays transparent
    // over the fallback image for the whole visit. Read the state off the
    // element rather than trusting an event that may predate us.
    if (!this.video.paused && !this.video.ended) this.onPlay(this.video)

    this.armAutoplayUnlock()
    this.initSoundToggle()
  }

  // The active video is the one not hidden by the breakpoint CSS.
  selectActiveVideo(): HTMLVideoElement {
    return (
      this.videos.find((video) => getComputedStyle(video).display !== 'none') ??
      this.videos[0]
    )
  }

  /**
   * Mobile can refuse muted autoplay outright — iOS Low Power Mode, Android Data
   * Saver, some content settings — and there is no event for it: play() simply
   * rejects and nothing ever tries again, leaving the poster frozen on screen.
   * The first touch or click anywhere is a user gesture the policy accepts, so
   * retry there. Disarms itself as soon as anything actually plays.
   */
  armAutoplayUnlock() {
    if (this.unlockAutoplay) return

    this.unlockAutoplay = () => {
      if (this.isPlaying || !this.inView || !this.autoPlayEnabled) return

      this.attemptPlay()
    }

    window.addEventListener('touchstart', this.unlockAutoplay, { passive: true })
    window.addEventListener('click', this.unlockAutoplay)
  }

  disarmAutoplayUnlock() {
    if (!this.unlockAutoplay) return

    window.removeEventListener('touchstart', this.unlockAutoplay)
    window.removeEventListener('click', this.unlockAutoplay)
    this.unlockAutoplay = null
  }

  initSoundToggle() {
    if (!this.soundToggle || !this.video) return

    // Progressive enhancement: only reveal the control once JS can drive it.
    // Unhide first, then fade in on the next frame (opacity reveal, no slide).
    this.soundToggle.hidden = false
    const toggle = this.soundToggle
    requestAnimationFrame(() => toggle.classList.add('is-revealed'))
    this.soundToggle.addEventListener('click', this.toggleMute.bind(this))
    this.updateSoundState()
  }

  toggleMute() {
    if (!this.video) return

    const nextMuted = !this.video.muted
    this.video.muted = nextMuted

    // Unmuting counts as the user gesture that unlocks audio — make sure it plays
    if (!nextMuted) this.attemptPlay()

    this.updateSoundState()
  }

  updateSoundState() {
    if (!this.soundToggle || !this.video) return

    const soundOn = !this.video.muted
    setAriaState(this.soundToggle, 'aria-pressed', soundOn)

    const label = soundOn
      ? this.soundToggle.dataset.labelMute
      : this.soundToggle.dataset.labelUnmute
    if (label) this.soundToggle.setAttribute('aria-label', label)
  }

  get isPlaying() {
    return this.video && !this.video.paused
  }

  async attemptPlay() {
    if (!this.video) return

    // Hold the reference across the await — a breakpoint change can reassign
    // this.video while play() is still pending
    const video = this.video

    // Never let the off-breakpoint video play in the background
    this.videos.forEach((other) => {
      if (other !== video && !other.paused) other.pause()
    })

    if (this.isPlaying) {
      this.onPlay(video)

      return
    }

    try {
      await video.play()

      if (video.paused) {
        throw new Error('Autoplay resolved but not advancing (likely blocked)')
      }

      // Reveal from the resolved promise rather than waiting on `playing`,
      // which may already have fired before this component existed
      this.onPlay(video)
    }
    catch (e: unknown) {
      console.warn('Autoplay blocked or failed:', e)
    }
  }

  togglePlay() {
    if (!this.video) return

    if (this.isPlaying) {
      this.video.pause()
    }
    else {
      this.attemptPlay()
    }
  }

  onCanPlay(video: HTMLVideoElement) {
    if (video === this.video && this.inView && !this.isPlaying && this.autoPlayEnabled) {
      this.attemptPlay()
    }
  }

  onPlay(video: HTMLVideoElement) {
    // Only the video for the current breakpoint may claim the frame. Marking the
    // display:none one would retire the fallback image via
    // `.graphic-cover:has(video.is-ready) img` while nothing visible is playing.
    if (this.video && video !== this.video) return

    video.classList.add(classes.isReady)
    this.disarmAutoplayUnlock()
  }

  onError(e: Event, video: HTMLVideoElement) {
    console.warn('Video error', e)
    // Dropping is-ready brings the fallback image back if the video dies mid-playback
    video.classList.remove(classes.isReady)
    video.style.display = 'none'
  }

  onBreakpointChange() {
    const next = this.selectActiveVideo()
    if (next === this.video) return

    // Hand the frame back to the fallback image until the incoming video is playing
    this.video?.classList.remove(classes.isReady)
    this.video?.pause()
    this.video = next
    this.video.preload = 'auto'
    this.updateSoundState()

    if (this.inView && this.autoPlayEnabled) this.attemptPlay()
  }

  async onVisibilityChange(visible = false) {
    this.inView = visible

    if (!this.video) return

    if (this.inView) {
      this.hasBeenInView = true
      this.video.preload = 'auto'

      if (!this.autoPlayEnabled) return

      await this.attemptPlay()
    }
    else {
      // The observer's first callback can report "not intersecting" before layout
      // has settled. Pausing on that would stop an autoplay that had just begun
      // and leave the poster frozen, so only pause once we have actually been in
      // view and are genuinely leaving it.
      if (!this.hasBeenInView) return

      this.videos.forEach((video) => video.pause())
      this.video.preload = 'metadata'
    }
  }

  onIntersection(entries: IntersectionObserverEntry[]) {
    this.onVisibilityChange(entries[0].isIntersecting)
  }

  destroy() {
    this.disarmAutoplayUnlock()

    super.destroy()
  }
}
